import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import type { Lead, LeadTag } from "@/types/lead";
import * as XLSX from "xlsx";

const EXPORT_PAGE_SIZE = 500;

const LEAD_SELECT =
  "id, name, email, phone, stage, notes, assigned_agent_id, assigned_member_id, created_at, updated_at, current_appointment_id, current_appointment_date, order_index, metadata, funnel_id, workspace_id";

export type LeadsExportScope =
  | "all_workspace"
  | "all_funnel"
  | "stage"
  | "current_filter"
  | "selected";

export interface LeadsExportContext {
  workspaceId: string;
  dataMode: "funnels" | "groups";
  activeFunnelId: string | null;
  isIntegrationFunnel: boolean;
  isDefaultFunnel: boolean;
  selectedGroupId: string | null;
  /** Filtros ativos na tela (usados em current_filter) */
  search: string;
  stageFilter: string;
  tagIds: string[];
  sortOption: string;
  selectedLeadIds: string[];
  scope: LeadsExportScope;
  /** Obrigatório quando scope === stage */
  exportStageSlug?: string | null;
}

export function leadToExportRow(lead: Lead) {
  const meta = (lead.metadata as Record<string, unknown>) || {};
  return {
    Nome: lead.name || "",
    Email: lead.email || "",
    Telefone: lead.phone || "",
    Estágio: lead.stage || "",
    "ID Funil": lead.funnel_id || "",
    Notas: lead.notes || "",
    "Razão Social": String(meta.razao_social ?? ""),
    CNPJ: String(meta.cnpj ?? ""),
    "Cidade/Estado": String(meta.cidade_estado ?? ""),
    "Data de Nascimento": String(meta.birthday ?? ""),
    Empresa: String(meta.company ?? ""),
    Cargo: String(meta.position ?? ""),
    Instagram: meta.instagram_username ? `@${meta.instagram_username}` : "",
    Origem: String(meta.source ?? lead.channel ?? ""),
    "Criado em": lead.created_at ? new Date(lead.created_at).toLocaleString("pt-BR") : "",
    ...customFieldExportColumns(meta),
  };
}

function customFieldExportColumns(meta: Record<string, unknown>): Record<string, string> {
  const nested = meta.custom_fields && typeof meta.custom_fields === "object" && !Array.isArray(meta.custom_fields)
    ? (meta.custom_fields as Record<string, unknown>)
    : {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(nested)) {
    out[`cf_${key}`] = value == null ? "" : String(value);
  }
  return out;
}

async function fetchTagsByLeadIds(
  supabase: SupabaseClient<Database>,
  leadIds: string[],
): Promise<Map<string, LeadTag[]>> {
  const tagsByLead = new Map<string, LeadTag[]>();
  if (leadIds.length === 0) return tagsByLead;

  const chunkSize = 500;
  for (let i = 0; i < leadIds.length; i += chunkSize) {
    const chunk = leadIds.slice(i, i + chunkSize);
    const { data, error } = await supabase
      .from("lead_tag_assignments")
      .select("lead_id, tag:tag_id (id, name, color)")
      .in("lead_id", chunk);
    if (error) throw error;
    for (const row of (data as { lead_id: string; tag: LeadTag | null }[]) || []) {
      if (!row.lead_id || !row.tag) continue;
      if (!tagsByLead.has(row.lead_id)) tagsByLead.set(row.lead_id, []);
      tagsByLead.get(row.lead_id)!.push(row.tag);
    }
  }
  return tagsByLead;
}

function applyTagFilter(leads: Lead[], tagIds: string[]): Lead[] {
  if (tagIds.length === 0) return leads;
  return leads.filter((lead) => {
    const leadTagIds = new Set((lead.tags || []).map((t) => t.id));
    return tagIds.every((id) => leadTagIds.has(id));
  });
}

function integrationCursorKey(
  cursor: { order_index: number; lead_id: string } | null,
): string | null {
  return cursor ? `${cursor.order_index}:${cursor.lead_id}` : null;
}

const MAX_EXPORT_PAGES = 500;

function assertOffsetPaginationAdvanced(
  hasMore: boolean,
  pageLeadCount: number,
  prevOffset: number,
  nextOffset: number,
): void {
  if (!hasMore || pageLeadCount === 0) return;
  if (prevOffset === nextOffset) {
    throw new Error("Exportação interrompida: a paginação repetiu a mesma página de leads.");
  }
}

async function fetchFunnelLeadsPage(
  supabase: SupabaseClient<Database>,
  opts: {
    workspaceId: string;
    activeFunnelId: string | null;
    isIntegrationFunnel: boolean;
    isDefaultFunnel: boolean;
    search: string | null;
    stage: string | null;
    sortOption: string;
    offset: number;
    integrationCursor: { order_index: number; lead_id: string } | null;
  },
): Promise<{
  leads: Lead[];
  hasMore: boolean;
  nextOffset: number;
  nextIntegrationCursor: { order_index: number; lead_id: string } | null;
}> {
  const { workspaceId, activeFunnelId, isIntegrationFunnel, isDefaultFunnel } = opts;

  if (isIntegrationFunnel && activeFunnelId) {
    const limitPlusOne = EXPORT_PAGE_SIZE + 1;
    let membershipQuery = supabase
      .from("lead_funnel_memberships")
      .select("lead_id, stage, order_index")
      .eq("funnel_id", activeFunnelId)
      .order("order_index", { ascending: true })
      .order("lead_id", { ascending: true })
      .limit(limitPlusOne);

    if (opts.integrationCursor) {
      const { order_index, lead_id } = opts.integrationCursor;
      membershipQuery = membershipQuery.or(
        `order_index.gt.${order_index},and(order_index.eq.${order_index},lead_id.gt."${lead_id}")`,
      );
    }

    const { data: memberships, error: membershipsError } = await membershipQuery;
    if (membershipsError) throw membershipsError;

    const slice = (memberships || []).slice(0, EXPORT_PAGE_SIZE);
    const hasMore = (memberships || []).length > EXPORT_PAGE_SIZE;
    let nextIntegrationCursor = opts.integrationCursor;
    if (slice.length > 0) {
      const last = slice[slice.length - 1];
      nextIntegrationCursor = {
        order_index: Number(last.order_index ?? 0),
        lead_id: String(last.lead_id),
      };
    }

    const leadIds = slice.map((m) => String(m.lead_id)).filter(Boolean);
    if (leadIds.length === 0) {
      return {
        leads: [],
        hasMore,
        nextOffset: opts.offset,
        nextIntegrationCursor,
      };
    }

    let leadsQuery = supabase.from("leads").select(LEAD_SELECT).eq("workspace_id", workspaceId).in("id", leadIds);
    if (opts.search) {
      const escaped = opts.search.split(",").join(" ");
      leadsQuery = leadsQuery.or(
        `name.ilike.%${escaped}%,email.ilike.%${escaped}%,phone.ilike.%${escaped}%`,
      );
    }

    const { data: leadRows, error: leadRowsError } = await leadsQuery;
    if (leadRowsError) throw leadRowsError;

    const membershipByLeadId = new Map(slice.map((m) => [String(m.lead_id), m]));
    let pageLeads = (leadRows || []).map((row) => {
      const membership = membershipByLeadId.get(String(row.id));
      return {
        ...row,
        stage: membership?.stage || row.stage,
        order_index: membership?.order_index ?? row.order_index ?? 0,
      } as Lead;
    });

    if (opts.stage) {
      pageLeads = pageLeads.filter((l) => l.stage === opts.stage);
    }
    pageLeads.sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

    return {
      leads: pageLeads,
      hasMore,
      nextOffset: opts.offset + pageLeads.length,
      nextIntegrationCursor,
    };
  }

  const funnelIdToFilter = activeFunnelId;
  const includeLegacyNullFunnel = Boolean(funnelIdToFilter && isDefaultFunnel);
  const rangeEnd = opts.offset + EXPORT_PAGE_SIZE - 1;

  let q = supabase
    .from("leads")
    .select(LEAD_SELECT)
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .range(opts.offset, rangeEnd);

  if (includeLegacyNullFunnel && funnelIdToFilter) {
    if (opts.search) {
      const escaped = opts.search.split(",").join(" ");
      q = q.or(
        `and(funnel_id.eq.${funnelIdToFilter},name.ilike.%${escaped}%),and(funnel_id.eq.${funnelIdToFilter},email.ilike.%${escaped}%),and(funnel_id.eq.${funnelIdToFilter},phone.ilike.%${escaped}%),and(funnel_id.is.null,name.ilike.%${escaped}%),and(funnel_id.is.null,email.ilike.%${escaped}%),and(funnel_id.is.null,phone.ilike.%${escaped}%)`,
      );
    } else {
      q = q.or(`funnel_id.eq.${funnelIdToFilter},funnel_id.is.null`);
    }
  } else {
    if (funnelIdToFilter) q = q.eq("funnel_id", funnelIdToFilter);
    if (opts.search) {
      const escaped = opts.search.split(",").join(" ");
      q = q.or(`name.ilike.%${escaped}%,email.ilike.%${escaped}%,phone.ilike.%${escaped}%`);
    }
  }
  if (opts.stage) q = q.eq("stage", opts.stage);

  const { data: fallbackData, error: fallbackError } = await q;
  if (fallbackError) throw fallbackError;
  let pageLeads = (fallbackData || []) as Lead[];

  const hasMore = pageLeads.length === EXPORT_PAGE_SIZE;

  return {
    leads: pageLeads,
    hasMore,
    nextOffset: opts.offset + pageLeads.length,
    nextIntegrationCursor: opts.integrationCursor,
  };
}

async function fetchGroupLeadsAll(
  supabase: SupabaseClient<Database>,
  workspaceId: string,
  groupId: string,
): Promise<Lead[]> {
  const { data, error } = await supabase
    .from("lead_group_memberships")
    .select(`lead_id, leads (${LEAD_SELECT})`)
    .eq("group_id", groupId);

  if (error) throw error;

  const leads: Lead[] = [];
  for (const row of data || []) {
    const lead = (row as { leads: Lead | null }).leads;
    if (lead && lead.workspace_id === workspaceId) {
      leads.push(lead);
    }
  }
  return leads;
}

async function fetchWorkspaceLeadsPage(
  supabase: SupabaseClient<Database>,
  workspaceId: string,
  offset: number,
): Promise<{
  leads: Lead[];
  hasMore: boolean;
  nextOffset: number;
}> {
  const rangeEnd = offset + EXPORT_PAGE_SIZE - 1;
  const { data, error } = await supabase
    .from("leads")
    .select(LEAD_SELECT)
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .range(offset, rangeEnd);

  if (error) throw error;

  const pageLeads = (data || []) as Lead[];
  const hasMore = pageLeads.length === EXPORT_PAGE_SIZE;

  return {
    leads: pageLeads,
    hasMore,
    nextOffset: offset + pageLeads.length,
  };
}

export async function fetchWorkspaceLeadCount(
  supabase: SupabaseClient<Database>,
  workspaceId: string,
): Promise<number> {
  const { count, error } = await supabase
    .from("leads")
    .select("id", { count: "exact", head: true })
    .eq("workspace_id", workspaceId);
  if (error) throw error;
  return count ?? 0;
}

async function fetchLeadsByIds(
  supabase: SupabaseClient<Database>,
  workspaceId: string,
  ids: string[],
): Promise<Lead[]> {
  const results: Lead[] = [];
  const chunkSize = 500;
  for (let i = 0; i < ids.length; i += chunkSize) {
    const chunk = ids.slice(i, i + chunkSize);
    const { data, error } = await supabase
      .from("leads")
      .select(LEAD_SELECT)
      .eq("workspace_id", workspaceId)
      .in("id", chunk);
    if (error) throw error;
    results.push(...((data || []) as Lead[]));
  }
  return results;
}

export async function fetchLeadsForExport(
  supabase: SupabaseClient<Database>,
  ctx: LeadsExportContext,
  onProgress?: (count: number) => void,
): Promise<Lead[]> {
  if (!ctx.workspaceId) return [];

  if (ctx.scope === "selected") {
    const ids = [...ctx.selectedLeadIds];
    if (ids.length === 0) return [];
    const rows = await fetchLeadsByIds(supabase, ctx.workspaceId, ids);
    const tagsByLead = await fetchTagsByLeadIds(
      supabase,
      rows.map((l) => l.id),
    );
    return rows.map((l) => ({ ...l, tags: tagsByLead.get(l.id) || [] }));
  }

  if (ctx.scope === "all_workspace") {
    const all: Lead[] = [];
    let offset = 0;
    let hasMore = true;

    let pageNum = 0;
    while (hasMore) {
      if (++pageNum > MAX_EXPORT_PAGES) {
        throw new Error("Exportação interrompida: limite de páginas atingido.");
      }
      const prevOffset = offset;
      const page = await fetchWorkspaceLeadsPage(supabase, ctx.workspaceId, offset);
      assertOffsetPaginationAdvanced(page.hasMore, page.leads.length, prevOffset, page.nextOffset);

      const pageIds = page.leads.map((l) => l.id);
      const tagsByLead = await fetchTagsByLeadIds(supabase, pageIds);
      const withTags = page.leads.map((l) => ({
        ...l,
        tags: tagsByLead.get(l.id) || [],
      }));

      all.push(...withTags);
      onProgress?.(all.length);

      hasMore = page.hasMore;
      offset = page.nextOffset;
      if (page.leads.length === 0) break;
    }

    return all;
  }

  if (ctx.dataMode === "groups") {
    if (!ctx.selectedGroupId) return [];
    return fetchGroupLeadsAll(supabase, ctx.workspaceId, ctx.selectedGroupId);
  }

  if (!ctx.activeFunnelId) return [];

  let search: string | null = null;
  let stage: string | null = null;
  let tagIds: string[] = [];

  switch (ctx.scope) {
    case "all_funnel":
      break;
    case "stage":
      stage = ctx.exportStageSlug || null;
      break;
    case "current_filter":
      search = ctx.search.trim() || null;
      stage = ctx.stageFilter !== "all" ? ctx.stageFilter : null;
      tagIds = ctx.tagIds;
      break;
  }

  const all: Lead[] = [];
  let offset = 0;
  let integrationCursor: { order_index: number; lead_id: string } | null = null;
  let hasMore = true;

  let pageNum = 0;
  while (hasMore) {
    if (++pageNum > MAX_EXPORT_PAGES) {
      throw new Error("Exportação interrompida: limite de páginas atingido.");
    }
    const prevOffset = offset;
    const prevIntegrationCursor = integrationCursor;
    const page = await fetchFunnelLeadsPage(supabase, {
      workspaceId: ctx.workspaceId,
      activeFunnelId: ctx.activeFunnelId,
      isIntegrationFunnel: ctx.isIntegrationFunnel,
      isDefaultFunnel: ctx.isDefaultFunnel,
      search,
      stage,
      sortOption: ctx.sortOption,
      offset,
      integrationCursor,
    });

    if (ctx.isIntegrationFunnel) {
      if (
        page.hasMore &&
        page.leads.length > 0 &&
        integrationCursorKey(prevIntegrationCursor) ===
          integrationCursorKey(page.nextIntegrationCursor)
      ) {
        throw new Error("Exportação interrompida: a paginação repetiu a mesma página de leads.");
      }
    } else {
      assertOffsetPaginationAdvanced(page.hasMore, page.leads.length, prevOffset, page.nextOffset);
    }

    const pageIds = page.leads.map((l) => l.id);
    const tagsByLead = await fetchTagsByLeadIds(supabase, pageIds);
    let withTags = page.leads.map((l) => ({
      ...l,
      tags: tagsByLead.get(l.id) || [],
    }));
    withTags = applyTagFilter(withTags as Lead[], tagIds) as typeof withTags;

    all.push(...withTags);
    onProgress?.(all.length);

    hasMore = page.hasMore;
    offset = page.nextOffset;
    integrationCursor = page.nextIntegrationCursor;

    if (page.leads.length === 0) break;
  }

  return all;
}

export function downloadLeadsXlsx(leads: Lead[], filenamePrefix = "leads_export") {
  const dataToExport = leads.map(leadToExportRow);
  const worksheet = XLSX.utils.json_to_sheet(dataToExport);
  worksheet["!cols"] = [
    { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 15 }, { wch: 30 },
    { wch: 30 }, { wch: 22 }, { wch: 20 }, { wch: 15 }, { wch: 20 },
    { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 18 },
  ];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
  XLSX.writeFile(workbook, `${filenamePrefix}_${new Date().toISOString().split("T")[0]}.xlsx`);
  return dataToExport.length;
}
